import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { TabularReportScreen } from '@/components/tabular-report/tabular-report-screen'
import { hasGrant } from '@/lib/me'

const date = (value: unknown) =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined

/** Reports the server answers under /inventory/insights (WP08). */
export const INSIGHT_REPORTS = [
  'stock-by-category',
  'supplier-purchases',
  'sales-by-staff',
  'landed-cost',
  'gross-margin',
  'branch-comparison',
  'serial-inventory',
  'warranty-ending',
] as const

/** Reports of things as they are now, with no period to choose. */
const NO_PERIOD: readonly string[] = ['stock-by-category', 'serial-inventory', 'warranty-ending']

/**
 * Route shell for one trade report (WP08). The period lives in the URL. The
 * grant guard is presentation only: the API enforces `inventory_report.view`.
 */
export const Route = createFileRoute('/_authenticated/inventory/reports/insights/$report')({
  validateSearch: (search: Record<string, unknown>) => ({
    from: date(search.from),
    to: date(search.to),
  }),
  beforeLoad: ({ context, params }) => {
    if (
      !hasGrant(context.queryClient, 'inventory_report.view') ||
      !(INSIGHT_REPORTS as readonly string[]).includes(params.report)
    ) {
      throw redirect({ to: '/inventory/reports' })
    }
  },
  component: InsightReportRoute,
})

function InsightReportRoute() {
  const { report } = Route.useParams()
  const navigate = useNavigate({ from: Route.fullPath })

  return (
    <TabularReportScreen
      path={`/inventory/insights/${report}`}
      trail={[{ label: 'inventory:screens.reports.index.title', href: '/inventory/reports' }]}
      search={Route.useSearch()}
      onSearchChange={(next) => void navigate({ search: { from: next.from, to: next.to } })}
      period={NO_PERIOD.includes(report) ? 'none' : 'range'}
      exportPermission="inventory_report.export"
    />
  )
}
